/**
 * Shared animation variants and utilities for consistent Apple-like animations
 * across the entire UnlockEd application.
 */

// Apple-inspired easing curve - feels natural and premium
export const appleEase = [0.22, 1, 0.36, 1] as const;

// Spring configuration for modals and cards
export const springConfig = {
  type: "spring" as const,
  damping: 25,
  stiffness: 400,
};

// Gentle spring for larger elements
export const gentleSpring = {
  type: "spring" as const,
  damping: 30,
  stiffness: 200,
};

// Page transition variants
export const pageVariants = {
  initial: { opacity: 0, y: 12 },
  animate: { 
    opacity: 1, 
    y: 0, 
    transition: { duration: 0.4, ease: appleEase } 
  },
  exit: { 
    opacity: 0, 
    y: -8, 
    transition: { duration: 0.3, ease: appleEase } 
  },
};

// Stagger container for lists/grids of items
export const staggerContainer = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.08,
      delayChildren: 0.1,
    },
  },
};

// Fast stagger for quick lists
export const fastStagger = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.05,
    },
  },
};

// Fade up animation for individual items
export const fadeInUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { 
    opacity: 1, 
    y: 0, 
    transition: { duration: 0.5, ease: appleEase } 
  },
};

// Smaller fade up for cards
export const fadeInUpSmall = {
  hidden: { opacity: 0, y: 12 },
  visible: { 
    opacity: 1, 
    y: 0, 
    transition: { duration: 0.4, ease: appleEase } 
  },
};

// Scale in with fade - great for modals and cards
export const scaleIn = {
  hidden: { opacity: 0, scale: 0.95 },
  visible: { 
    opacity: 1, 
    scale: 1, 
    transition: springConfig 
  },
};

// Scale in from center - for pop effects
export const popIn = {
  hidden: { opacity: 0, scale: 0.9 },
  visible: { 
    opacity: 1, 
    scale: 1, 
    transition: { 
      type: "spring",
      damping: 20,
      stiffness: 300,
    } 
  },
};

// Slide in from right
export const slideInRight = {
  hidden: { opacity: 0, x: 20 },
  visible: { 
    opacity: 1, 
    x: 0, 
    transition: { duration: 0.4, ease: appleEase } 
  },
};

// Slide in from left
export const slideInLeft = {
  hidden: { opacity: 0, x: -20 },
  visible: { 
    opacity: 1, 
    x: 0, 
    transition: { duration: 0.4, ease: appleEase } 
  },
};

// Header slide down animation
export const slideDown = {
  hidden: { opacity: 0, y: -10 },
  visible: { 
    opacity: 1, 
    y: 0, 
    transition: { duration: 0.3, ease: appleEase } 
  },
};

// Line reveal for text
export const lineReveal = {
  hidden: { opacity: 0, y: 10 },
  visible: { 
    opacity: 1, 
    y: 0, 
    transition: { duration: 0.6, ease: appleEase } 
  },
};

// Hover scale effect - subtle but noticeable
export const hoverScale = {
  scale: 1.02,
  transition: { duration: 0.2, ease: appleEase },
};

// Hover lift effect with shadow (use with className for shadow)
export const hoverLift = {
  y: -2,
  transition: { duration: 0.2, ease: appleEase },
};

// Tap effect for buttons
export const tapScale = {
  scale: 0.98,
  transition: { duration: 0.1 },
};

// Card hover animation preset
export const cardHover = {
  rest: { 
    scale: 1, 
    y: 0,
    transition: { duration: 0.2, ease: appleEase } 
  },
  hover: { 
    scale: 1.01, 
    y: -2,
    transition: { duration: 0.2, ease: appleEase } 
  },
};

// Mobile menu variants
export const mobileMenuVariants = {
  closed: { 
    opacity: 0, 
    height: 0,
    transition: { duration: 0.3, ease: appleEase } 
  },
  open: { 
    opacity: 1, 
    height: "auto",
    transition: { duration: 0.3, ease: appleEase } 
  },
};

// Modal/dialog variants
export const modalVariants = {
  hidden: { 
    opacity: 0, 
    scale: 0.95,
    y: 10,
  },
  visible: { 
    opacity: 1, 
    scale: 1,
    y: 0,
    transition: springConfig,
  },
  exit: { 
    opacity: 0, 
    scale: 0.95,
    y: 10,
    transition: { duration: 0.2, ease: appleEase },
  },
};

// Toast animation
export const toastVariants = {
  hidden: { 
    opacity: 0, 
    y: 20, 
    scale: 0.95 
  },
  visible: { 
    opacity: 1, 
    y: 0, 
    scale: 1,
    transition: springConfig,
  },
  exit: { 
    opacity: 0, 
    y: 20, 
    scale: 0.95,
    transition: { duration: 0.2, ease: appleEase },
  },
};

// Skeleton shimmer animation (for loading states)
export const shimmer = {
  hidden: { backgroundPosition: "-200% 0" },
  visible: { 
    backgroundPosition: "200% 0",
    transition: { 
      repeat: Infinity, 
      duration: 1.5, 
      ease: "linear" 
    },
  },
};

// Check mark animation
export const checkMark = {
  hidden: { pathLength: 0, opacity: 0 },
  visible: { 
    pathLength: 1, 
    opacity: 1,
    transition: { duration: 0.3, ease: appleEase },
  },
};

// Number count animation helper
export const countTransition = {
  duration: 0.8,
  ease: appleEase,
};

// Progress bar animation
export const progressBar = {
  hidden: { scaleX: 0, originX: 0 },
  visible: { 
    scaleX: 1,
    transition: { duration: 0.8, ease: appleEase },
  },
};

// Utility function to create delayed variants
export const withDelay = (variant: Record<string, any>, delay: number) => ({
  ...variant,
  visible: {
    ...variant.visible,
    transition: {
      ...variant.visible?.transition,
      delay,
    },
  },
});

// Duration presets
export const durations = {
  fast: 0.15,
  normal: 0.3,
  slow: 0.5,
  slower: 0.8,
};
